import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";

export type BiometricDeviceProtocol = "tcp" | "udp" | "http" | "https" | "serial";

export interface BiometricDeviceRecord {
  id: ID;
  model: string;
  driver: string | null;
  wrapperClass: string | null;
  protocol: BiometricDeviceProtocol;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export const BIOMETRIC_DEVICE_PROTOCOLS: BiometricDeviceProtocol[] = ["tcp", "udp", "http", "https", "serial"];

export const INVENTORY_BIOMETRIC_DEVICES_LS_KEY = "inventory.biometricDevices.v1";
const LS_KEY = INVENTORY_BIOMETRIC_DEVICES_LS_KEY;

type BiometricDeviceApiRecord = {
  id: number | string;
  model: string;
  driver?: string | null;
  wrapper_class?: string | null;
  protocol?: string | null;
  description?: string | null;
  created_at?: string;
  updated_at?: string;
};

type ListBiometricDevicesApiResponse = {
  data?: BiometricDeviceApiRecord[];
  meta?: { page?: number; per_page?: number; total?: number };
};

type UpsertApiResponse = { message?: string; data?: BiometricDeviceApiRecord };
type DeleteApiResponse = { message?: string };
type ShowApiResponse = { data?: BiometricDeviceApiRecord };

export type BiometricDeviceUpsertPayload = {
  model: string;
  driver?: string | null;
  wrapperClass?: string | null;
  protocol: BiometricDeviceProtocol;
  description?: string | null;
};

function normalizeProtocol(raw: string | null | undefined): BiometricDeviceProtocol {
  const p = (raw ?? "tcp").toLowerCase();
  return BIOMETRIC_DEVICE_PROTOCOLS.includes(p as BiometricDeviceProtocol) ? (p as BiometricDeviceProtocol) : "tcp";
}

function mapApiRecord(r: BiometricDeviceApiRecord): BiometricDeviceRecord {
  return {
    id: String(r.id),
    model: (r.model ?? "").trim(),
    driver: r.driver?.trim() ? r.driver.trim() : null,
    wrapperClass: r.wrapper_class?.trim() ? r.wrapper_class.trim() : null,
    protocol: normalizeProtocol(r.protocol),
    description: r.description?.trim() ? r.description.trim() : null,
    createdAt: r.created_at || new Date().toISOString(),
    updatedAt: r.updated_at || r.created_at || new Date().toISOString(),
  };
}

function dedupe(items: BiometricDeviceRecord[]): BiometricDeviceRecord[] {
  const m = new Map<ID, BiometricDeviceRecord>();
  for (const x of items) m.set(x.id, x);
  return [...m.values()];
}

function load(): BiometricDeviceRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const p = JSON.parse(raw) as BiometricDeviceRecord[];
    return Array.isArray(p) ? dedupe(p) : [];
  } catch {
    return [];
  }
}

function save(items: BiometricDeviceRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupe(items)));
  window.dispatchEvent(new CustomEvent("inventory_biometric_devices_cache_changed"));
}

export function listBiometricDeviceRecords(): BiometricDeviceRecord[] {
  return load();
}

export function getBiometricDeviceById(id: ID): BiometricDeviceRecord | undefined {
  return load().find((r) => String(r.id) === String(id));
}

function toApiPayload(p: BiometricDeviceUpsertPayload) {
  return {
    model: p.model.trim(),
    driver: p.driver?.trim() || null,
    wrapper_class: p.wrapperClass?.trim() || null,
    protocol: p.protocol,
    description: p.description?.trim() || null,
  };
}

export async function syncBiometricDevicesFromApi(): Promise<BiometricDeviceRecord[]> {
  const response = await api.get<ListBiometricDevicesApiResponse>("/api/v1/biometric-devices", { params: { per_page: 200 } });
  const list = (response.data?.data ?? []).map(mapApiRecord);
  save(list);
  return listBiometricDeviceRecords();
}

export async function fetchBiometricDeviceById(id: ID): Promise<BiometricDeviceRecord> {
  const res = await api.get<ShowApiResponse>(`/api/v1/biometric-devices/${id}`);
  const row = res.data?.data;
  if (!row) throw new Error("Invalid biometric device response.");
  const record = mapApiRecord(row);
  const items = load();
  const i = items.findIndex((r) => String(r.id) === String(record.id));
  if (i >= 0) items[i] = record;
  else items.push(record);
  save(items);
  return record;
}

export async function createBiometricDevice(payload: BiometricDeviceUpsertPayload) {
  const res = await api.post<UpsertApiResponse>("/api/v1/biometric-devices", toApiPayload(payload));
  const row = res.data?.data;
  if (!row) throw new Error("Invalid create biometric device response.");
  const record = mapApiRecord(row);
  const items = load();
  items.push(record);
  save(items);
  return { message: res.data?.message ?? "Biometric device created.", record };
}

export async function updateBiometricDevice(id: ID, payload: BiometricDeviceUpsertPayload) {
  const res = await api.put<UpsertApiResponse>(`/api/v1/biometric-devices/${id}`, toApiPayload(payload));
  const row = res.data?.data;
  if (!row) throw new Error("Invalid update biometric device response.");
  const record = mapApiRecord(row);
  const items = load();
  const i = items.findIndex((r) => String(r.id) === String(record.id));
  if (i >= 0) items[i] = record;
  else items.push(record);
  save(items);
  return { message: res.data?.message ?? "Biometric device updated.", record };
}

export async function deleteBiometricDevice(id: ID) {
  const res = await api.delete<DeleteApiResponse>(`/api/v1/biometric-devices/${id}`);
  const items = load().filter((r) => String(r.id) !== String(id));
  save(items);
  return { message: res.data?.message ?? "Biometric device deleted." };
}
